import type { ReactNode } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import styled from 'styled-components';

import { KycDocumentType } from '#src/__generated__/globalTypes.ts';
import type { KycDocumentRequirement } from '#src/__generated__/globalTypes.ts';
import type { IconDefinition } from '#src/atoms/icons/index.tsx';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { iconAddressCard } from '#src/atoms/icons/system/iconAddressCard.ts';
import { iconAlert } from '#src/atoms/icons/system/iconAlert.ts';
import { iconCamera } from '#src/atoms/icons/system/iconCamera.ts';
import { iconCreditCardBlank } from '#src/atoms/icons/system/iconCreditCardBlank.ts';
import { iconEye } from '#src/atoms/icons/system/iconEye.ts';
import { iconImage } from '#src/atoms/icons/system/iconImage.ts';
import { FormControlLabel } from '#src/atoms/inputs/FormControlLabel/index.tsx';
import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BodyM, BodyS, HeadlineL } from '#src/atoms/typography/index.tsx';
import { UploadInput } from '#src/components/fiatWallet/CreateFiatWallet/DocumentCheck/UploadInput/index.tsx';
import { StepWrapperFromProps } from '#src/components/fiatWallet/CreateFiatWallet/StepWrapper/index.tsx';
import type { GraphQLResult } from '#src/components/form/Form/index.tsx';
import { GraphqlForm } from '#src/components/form/Form/index.tsx';
import type { FileWithDataURL } from '#src/components/form/UploadFile/useUploadFile.ts';
import { kycDocumentTypeLabelsWithPossessivePronoun } from '#src/components/kyc/ProofOfIdentityForm/ChooseDocument/messages.ts';

import type { ImageQualityIssue } from './analyzeImageQuality.ts';
import { useImageQualityWarnings } from './useImageQualityWarnings.ts';

const messages = defineMessages({
  front: {
    defaultMessage: 'front',
  },
  back: {
    defaultMessage: 'back',
  },
});

const qualityIssueMessages = defineMessages<ImageQualityIssue>({
  blurry: {
    defaultMessage:
      'This photo looks blurry. Hold your device steady and retake it.',
  },
  greyscale: {
    defaultMessage:
      'This photo looks black & white. Upload a color photo instead.',
  },
  lowResolution: {
    defaultMessage:
      'This photo looks low resolution. Move closer or use a higher-quality photo.',
  },
});

const StyledGraphqlForm = styled(GraphqlForm)`
  margin-bottom: 0;
  height: 100%;
`;

const StyledFormControlLabel = styled(FormControlLabel)`
  > * {
    width: 100%;
  }
`;

const Tips = styled(Vertical).attrs({ gap: 1 })`
  width: 100%;
`;

const Tip = styled(Horizontal).attrs({ gap: 1 })`
  align-items: flex-start;
  color: var(--c-nd-600);
`;

const TipIcon = styled.span`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  height: 1lh;
  font: var(--t-body-m);
  color: var(--c-blue-400);
`;

const QualityWarning = styled(Horizontal).attrs({ gap: 0.5 })`
  align-items: flex-start;
  margin-top: var(--unit);
  color: var(--c-yellow-400);
`;

const WarningList = styled(BodyS).attrs({ as: 'ul' })`
  margin: 0;
  padding-left: 0;
  list-style: none;
`;

export type Props = {
  documentRequirement: KycDocumentRequirement;
  frontPage: FileWithDataURL | undefined;
  backPage: FileWithDataURL | undefined;
  setFrontPage: (file: FileWithDataURL) => void;
  setBackPage: (file: FileWithDataURL) => void;
  onBack: () => void;
  onSuccess: () => void;
  onSubmit: (onResult: (result: GraphQLResult) => void) => void;
};

const UploadFileWrapper = styled.div`
  display: flex;
  gap: var(--double-unit);
  align-items: flex-start;

  > * {
    flex-grow: 1;
    width: 50%;
  }
`;

type UploadFieldProps = {
  name: string;
  documentName: string;
  icon: IconDefinition;
  file: FileWithDataURL | undefined;
  setFile: (file: FileWithDataURL) => void;
  hint?: ReactNode;
};

const UploadField = ({
  name,
  documentName,
  icon,
  file,
  setFile,
  hint,
}: UploadFieldProps) => {
  const { formatMessage } = useIntl();
  const issues = useImageQualityWarnings(file);

  return (
    <StyledFormControlLabel
      label={null}
      control={
        <Vertical gap={0}>
          <UploadInput
            name={name}
            documentName={documentName}
            icon={icon}
            file={file}
            setFile={setFile}
            hint={hint}
          />
          {issues.length > 0 && (
            <QualityWarning role="status">
              <FontAwesomeIcon icon={iconAlert} />
              <WarningList>
                {issues.map(issue => (
                  <li key={issue}>
                    {formatMessage(qualityIssueMessages[issue])}
                  </li>
                ))}
              </WarningList>
            </QualityWarning>
          )}
        </Vertical>
      }
      labelPlacement="top"
    />
  );
};

export const Upload = ({
  documentRequirement,
  frontPage,
  backPage,
  setFrontPage,
  setBackPage,
  onSuccess,
  onSubmit,
  onBack,
}: Props) => {
  const { formatMessage } = useIntl();

  const filesCount = documentRequirement.nbPages;
  const filesUploadedCount = [frontPage, backPage].filter(Boolean).length;

  const isPassport =
    documentRequirement.documentType === KycDocumentType.PASSPORT;

  const formattedDocumentName = formatMessage(
    kycDocumentTypeLabelsWithPossessivePronoun[documentRequirement.documentType]
  );

  return (
    <StyledGraphqlForm
      onSuccess={onSuccess}
      onSubmit={(_, onResult) => {
        onSubmit(onResult);
      }}
      render={(Error, SubmitButton) => (
        <StepWrapperFromProps
          onBack={onBack}
          submitButton={
            <SubmitButton
              size="medium"
              disabled={filesUploadedCount !== filesCount}
            >
              <FormattedMessage defaultMessage="Submit ID" />
            </SubmitButton>
          }
        >
          <HeadlineL as="h3">
            <FormattedMessage
              defaultMessage="Upload {filesCount, plural, one {an image} other {images}} of {documentType}"
              values={{
                documentType: formattedDocumentName,
                filesCount,
              }}
            />
          </HeadlineL>
          <Tips>
            <Tip>
              <TipIcon>
                <FontAwesomeIcon icon={iconCamera} />
              </TipIcon>
              <BodyM>
                <FormattedMessage defaultMessage="Take a photo of the original document — don’t upload a scan or screenshot." />
              </BodyM>
            </Tip>
            <Tip>
              <TipIcon>
                <FontAwesomeIcon icon={iconEye} />
              </TipIcon>
              <BodyM>
                <FormattedMessage defaultMessage="Show the whole document, with nothing covering it or cut off at the edges." />
              </BodyM>
            </Tip>
            <Tip>
              <TipIcon>
                <FontAwesomeIcon icon={iconImage} />
              </TipIcon>
              <BodyM>
                <FormattedMessage defaultMessage="Use a well-lit spot so the photo comes out sharp and in color." />
              </BodyM>
            </Tip>
          </Tips>
          <Error />
          <UploadFileWrapper>
            <UploadField
              name="frontPage"
              documentName={
                filesCount === 1
                  ? formattedDocumentName
                  : formatMessage(messages.front)
              }
              icon={iconAddressCard}
              file={frontPage}
              setFile={setFrontPage}
              hint={
                isPassport ? (
                  <FormattedMessage defaultMessage="Capture both pages" />
                ) : undefined
              }
            />
            {filesCount === 2 && (
              <UploadField
                name="backPage"
                documentName={formatMessage(messages.back)}
                icon={iconCreditCardBlank}
                file={backPage}
                setFile={setBackPage}
              />
            )}
          </UploadFileWrapper>
        </StepWrapperFromProps>
      )}
    />
  );
};
